import { type AuthorizationResponse, AuthorizationStatus } from '@/api/model/authorization';
import { type Profile, UserRole, SubscriptionStatus } from '@/api/model/profile';
import { PersonRole } from '@/api/model/person'
import { apiAddress, JSON_MIME_TYPE } from '@/util/http-utils';
import { SubscriptionLevel } from '@/api/model/profile';
import { type GuestEntity } from '@/types/guest-entity';
import { useAuthorization, notAuthorized } from '@/hooks/useAuthorization.hook';
import ISO8601 from '@/util/iso-dates';

const OCTET_STREAM_TYPE = "application/octet-stream";

const GUEST_TARGET_STORAGE_KEY = "guest_target";

export type IdTokenFunction = () => Promise<string | null>;

export default class Authorizor {
    private static _guestTarget: GuestEntity | undefined = undefined;

    public static async authorize(getToken?: IdTokenFunction): Promise<AuthorizationResponse> {
        if (Authorizor.loading || !getToken) {
            return notAuthorized;
        }
        try {
            Authorizor.loading = true;
            if (Authorizor.authState === AuthorizationStatus.AUTHORIZED) {
                return Authorizor.authorization;
            }
            const idToken = await getToken();
            const response: Response = await fetch(apiAddress(`/security/authorize`),{
                headers: {
                    'Accept': JSON_MIME_TYPE,
                    'Content-Type': JSON_MIME_TYPE,
                    'Authorization': `Bearer ${idToken}`
                }
            });
            if (!response.ok) {
                throw new Error(`Unable to authorize: ${response.statusText}`);
            }
            const authResponse: AuthorizationResponse = await response.json();
            if (authResponse.status != AuthorizationStatus.AUTHORIZED || !authResponse.token) {
                throw new Error(response.statusText);
            }
            Authorizor.setAuthorization(authResponse);
        } catch(error) {
            console.error("Error in Authorizor.authorize:", error);
            Authorizor.setAuthorization(notAuthorized);
        }
        finally {
            Authorizor.loading = false;
        }
        return Authorizor.authorization;
    }

    public static get userRole(): UserRole {
        if (Authorizor.authState != AuthorizationStatus.AUTHORIZED) {
            return UserRole.UNKNOWN;
        }
        const status = Authorizor.subscriptionStatus;
        if (status == SubscriptionStatus.NONE || 
            status == SubscriptionStatus.EXPIRED
        ) {
            return UserRole.GUEST;
        }
        const sub = Authorizor.authorization.profile?.subscription!;
        switch (sub.type) {
            case SubscriptionLevel.STUDENT:
            case SubscriptionLevel.PRO:
                return UserRole.COMPETITOR;
            case SubscriptionLevel.ORGANIZER:
                return UserRole.ORGANIZER;
        }
        return UserRole.UNKNOWN;
    }

    public static setGuestTarget(target: GuestEntity) {
        Authorizor._guestTarget = target;
    }

    public static roleForJump(jumpId: string): PersonRole {
        if (Array.isArray(Authorizor.profile?.invitations)) {
            for (const invitation of Authorizor.profile?.invitations) {
                if (invitation.jumpId === jumpId) {
                    return invitation.role;
                }
            }
        }
        return PersonRole.NONE;
    }

    public static roleForCompetition(competitionId: string): PersonRole {
        if (Array.isArray(Authorizor.profile?.invitations)) {
            for (const invitation of Authorizor.profile?.invitations) {
                if (invitation.competitionId === competitionId) {
                    return invitation.role;
                }
            }
        }
        return PersonRole.NONE;
    }

    public static get guestEntity(): GuestEntity {
        if (Authorizor._guestTarget) {
            return Authorizor._guestTarget;
        }
        if (Array.isArray(Authorizor.profile?.invitations)) {
            for (const invitation of Authorizor.profile?.invitations) {
                if (invitation.role === PersonRole.REVIEWER) {
                    return {
                        role: invitation.role,
                        jumpId: invitation.jumpId
                    }
                } else if (invitation.role === PersonRole.JUDGE) {
                    return {
                        role: invitation.role,
                        competitionId: invitation.competitionId
                    }
                }
            }
        }
        return { role: PersonRole.NONE }
    }

    public static get subscriptionStatus(): SubscriptionStatus {
        const sub = Authorizor.authorization.profile?.subscription;
        if (!sub || (sub.type as SubscriptionLevel) == SubscriptionLevel.NONE) {
            return SubscriptionStatus.NONE;
        }

        if (!sub.expiration && !sub.trialExpiration) {
            console.warn("DATA INTEGRITY: Active subscription, but no expiration or trial expiration")
            return SubscriptionStatus.NONE; 
        }
        const nowISO = ISO8601.nowUTC();
        const expirationISO = sub.expiration;
        const now = new Date(nowISO);
        const expiration = new Date(expirationISO!);
        if (expiration >= now) {
            return SubscriptionStatus.ACTIVE;
        }
        const trialExpirationISO = sub.trialExpiration;
        const trialExpiration = new Date(trialExpirationISO!);
        if (trialExpiration >= now) {
            return SubscriptionStatus.TRIAL;
        }
        return SubscriptionStatus.EXPIRED;
    }

    private static get loading(): boolean {
        return useAuthorization.getState().loading;
    }

    private static set loading(loading: boolean) {
        useAuthorization.getState().setLoading(loading);
    }

    private static get authorization(): AuthorizationResponse {
        return useAuthorization.getState().auth;
    }

    public static get authState(): AuthorizationStatus {
        return Authorizor.authorization.status ?? AuthorizationStatus.NOT_AUTHORIZED;
    }

    public static get profile(): Profile | undefined {
        return Authorizor.authorization.profile;
    }

    public static async refreshProfile(): Promise<Profile | undefined> {
        const userId = Authorizor.profile?.id;
        if (userId == null) {
            console.error("User profile id not found.");
            return;
        }
        const response: Response = await fetch(apiAddress(`/profile/${userId}`),{
            headers: Authorizor.headers()
        });
        if (!response.ok) {
            throw new Error(`Unable to authorize: ${response.statusText}`);
        }
        const profile = await response.json();
        Authorizor.authorization.profile = profile;
        return profile;
    }

    public static deauthorize() {
        useAuthorization.getState().clearAuth();
    }

    private static setAuthorization(authResponse: AuthorizationResponse) {
        useAuthorization.getState().setAuth(authResponse);
    }

    public static saveState = (key: string) => {
        if (Authorizor.authorization) {
            sessionStorage.setItem(key, JSON.stringify(Authorizor.authorization));
        }
        sessionStorage.setItem(GUEST_TARGET_STORAGE_KEY, JSON.stringify(Authorizor._guestTarget));
    }

    public static restoreState = (key: string, clear: boolean = true) => {
        try {
            const authJson = sessionStorage.getItem(key);
            const guestTargetJson = sessionStorage.getItem(GUEST_TARGET_STORAGE_KEY);
            if (!authJson && !guestTargetJson) {
                console.warn("Authorizor.restoreState: no saved state found.")
                return;
            }
            if (authJson) {
                useAuthorization.getState().setAuth(JSON.parse(authJson));
            }
            if (guestTargetJson) {
                Authorizor._guestTarget = JSON.parse(guestTargetJson);
            }
        }
        catch(error) {
            console.error(`Error in Authorizor.restoreState: ${error}`)
        }
        finally {
            if (clear) {
                Authorizor.clearState(key);
            }
        }
    }

    private static clearState = (key: string) => {
        sessionStorage.removeItem(key);
        sessionStorage.removeItem(GUEST_TARGET_STORAGE_KEY);
    }

    public static headers(init?: Record<string,string>, accept: string = JSON_MIME_TYPE, contentType: string = JSON_MIME_TYPE): HeadersInit {
        if (!Authorizor.token) {
            throw new Error("Not authorized")
        }
        let result: Record<string,string> = init ?? {};
        result["Authorization"] = `Bearer ${Authorizor.token?.token}`;
        if (!result.hasOwnProperty("Accept")) {
            result["Accept"] = accept;
        }
        if (!result.hasOwnProperty("Content-Type")) {
            result["Content-Type"] = contentType;
        }
        return result;
    }

    public static get token() {
        return Authorizor.authorization?.token;
    }

    public static uploadHeaders(): HeadersInit {
        if (!Authorizor.token) {
            throw new Error("Not authorized")
        }
        let result: HeadersInit = {};
        result["Authorization"] = `Bearer ${Authorizor.token.token}`;
        if (!result.hasOwnProperty("Accept")) {
            result["Accept"] = JSON_MIME_TYPE;
        }
        return result;
    }

    public static binaryDataHeaders(): HeadersInit {
        if (!Authorizor.token) {
            throw new Error("Not authorized")
        }
        let result: HeadersInit = {};
        result["Authorization"] = `Bearer ${Authorizor.token.token}`;
        if (!result.hasOwnProperty("Accept")) {
            result["Accept"] = JSON_MIME_TYPE;
        }
        if (!result.hasOwnProperty("Content-Type")) {
            result["Content-Type"] = OCTET_STREAM_TYPE;
        }
        return result;
    }
}